import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { User, NotebookPen, FileText, BookOpen, Settings, LogOut, UserCog, FlaskConical, TrendingUp, MessageSquare, Package } from 'lucide-react';
import { toast } from 'sonner';

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);

      if (user) {
        const { data, error } = await supabase
          .from('users')
          .select('name, role')
          .eq('id', user.id)
          .single();
        
        if (data) {
          setUserRole(data.role);
          if (data.name) setUser({ ...user, name: data.name });
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserName = () => {
    if (user?.name) return user.name;
    if (!user) return 'User';
    return user.email?.split('@')[0] || 'User';
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Error logging out');
    }
  };

  const menuItems = [
    {
      icon: NotebookPen,
      label: 'Personal Notes',
      path: '/personal-notes',
      color: 'text-white'
    },
    {
      icon: FileText,
      label: 'Documents',
      path: '/documents',
      color: 'text-gray-300'
    },
    {
      icon: BookOpen,
      label: 'Daily Standup',
      path: '/logger',
      color: 'text-gray-300'
    }
  ];

  const comingSoonItems = [
    {
      icon: TrendingUp,
      label: 'Overview',
      color: 'text-gray-400'
    },
    {
      icon: MessageSquare,
      label: 'Team Chat',
      color: 'text-gray-400'
    },
    {
      icon: Package,
      label: 'Lab Store',
      color: 'text-gray-400'
    },
    {
      icon: FlaskConical,
      label: 'Project & Experiments',
      color: 'text-gray-400'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 lg:p-8">
      <div className="max-w-2xl mx-auto">
        {/* User Profile Card */}
        <div className="bg-black rounded-2xl border border-white/20 p-6 mb-6 shadow-[0_0_15px_rgba(255,255,255,0.05)]">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-black border border-white/20 ring-4 ring-white/5 flex items-center justify-center text-white font-bold text-3xl shadow-[0_0_20px_rgba(255,255,255,0.05)]">
              {user?.name ? user.name.charAt(0).toUpperCase() : <User size={32} />}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-semibold text-white mb-1">{getUserName()}</h2>
              <p className="text-sm text-gray-400 truncate">{user?.email}</p>
              {userRole && (
                <span className="inline-block mt-2 px-3 py-1 text-xs font-medium rounded-full bg-white/10 border border-white/20 text-white capitalize">
                  {userRole}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="bg-black border border-white/10 rounded-2xl overflow-hidden mb-6">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={() => navigate(item.path)}
                className="w-full flex items-center gap-4 px-6 py-4 hover:bg-white/5 active:bg-white/10 transition-colors border-b border-white/5 last:border-b-0"
              >
                <Icon size={24} className={item.color} />
                <span className="text-base font-medium text-gray-200">{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Coming Soon Features */}
        <div className="bg-black border border-white/10 rounded-2xl overflow-hidden mb-6">
          <div className="px-6 py-3 border-b border-white/10 bg-white/[0.02]">
            <span className="text-xs uppercase tracking-wide text-gray-400">Coming Soon</span>
          </div>
          {comingSoonItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                className="w-full flex items-center justify-between gap-4 px-6 py-4 border-b border-white/5 last:border-b-0 opacity-75"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <Icon size={24} className={item.color} />
                  <span className="text-base font-medium text-gray-300 truncate">{item.label}</span>
                </div>
                <span className="text-[11px] px-2 py-1 rounded-full border border-white/15 bg-white/5 text-gray-400">Soon</span>
              </div>
            );
          })}
        </div>

        {/* Settings & Admin */}
        <div className="bg-black border border-white/10 rounded-2xl overflow-hidden mb-6">
          {userRole === 'admin' && (
            <button
              onClick={() => navigate('/admin/users')}
              className="w-full flex items-center gap-4 px-6 py-4 hover:bg-white/5 active:bg-white/10 transition-colors border-b border-white/5"
            >
              <UserCog size={24} className="text-white" />
              <span className="text-base font-medium text-gray-200">User Management</span>
            </button>
          )}
          <button
            onClick={() => navigate('/settings')}
            className="w-full flex items-center gap-4 px-6 py-4 hover:bg-white/5 active:bg-white/10 transition-colors"
          >
            <Settings size={24} className="text-gray-300" />
            <span className="text-base font-medium text-gray-200">Settings</span>
          </button>
        </div>

        {/* Logout */}
        <div className="bg-[#0a0a0a] rounded-2xl border border-white/10 overflow-hidden">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-4 px-6 py-4 hover:bg-red-500/10 active:bg-red-500/20 transition-colors"
          >
            <LogOut size={24} className="text-red-400" />
            <span className="text-base font-medium text-red-400">Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
